import React, { useState } from 'react';
import { regenerateRecoveryCodes } from '../../services/mfa';
import { MfaRecoveryCodesDisplay } from '../MFASetup/MfaRecoveryCodesDisplay';
import { Button } from '@/shared/components/ui/button/Button';
import { MfaInput } from '../MFAVerify/MfaInputField';
import { useAuth } from '../../hooks/useAuth';

type PanelStep = 'idle' | 'prompt' | 'display';

export const MfaRecoveryCodesPanel: React.FC = () => {
    const { mfaMethod } = useAuth();
    const [step, setStep] = useState<PanelStep>('idle');
    const [otpCode, setOtpCode] = useState<string[]>(Array(6).fill(''));
    const [codes, setCodes] = useState<string[]>([]);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const inputsRef = React.useRef<Array<HTMLInputElement | null>>([]);

    const handleRegenerate = async () => {
        if (!mfaMethod || mfaMethod === 'sms') return;

        const token = otpCode.join('');
        if (token.length < 6) {
            setError('Please enter your 6-digit code');
            return;
        }

        setIsLoading(true);
        setError('');
        try {
            const result = await regenerateRecoveryCodes({
                method: mfaMethod as 'totp' | 'email',
                token,
            });
            setCodes(result.recovery_codes);
            setStep('display');
        } catch {
            setError('Invalid code. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOtpChange = (value: string, index: number) => {
        setError('');
        if (!/^\d?$/.test(value)) return;
        const newCode = [...otpCode];
        newCode[index] = value;
        setOtpCode(newCode);
        if (value && index < 5) {
            inputsRef.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
            inputsRef.current[index - 1]?.focus();
        }
    };

    const handleCancel = () => {
        setStep('idle');
        setOtpCode(Array(6).fill(''));
        setError('');
    };

    const handleConfirmCodes = () => {
        setCodes([]);
        setOtpCode(Array(6).fill(''));
        setStep('idle');
    };

    if (step === 'display') {
        return <MfaRecoveryCodesDisplay codes={codes} onConfirm={handleConfirmCodes} />;
    }

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-base font-semibold text-mp-foreground">
                    Recovery codes
                </h2>
                <p className="text-sm text-mp-muted mt-1">
                    Recovery codes can be used to access your account if you lose your
                    authentication device. Regenerating will invalidate all existing codes.
                </p>
            </div>

            {step === 'idle' && (
                <Button variant="secondary" size="sm" onClick={() => setStep('prompt')}>
                    Regenerate recovery codes
                </Button>
            )}

            {step === 'prompt' && (
                <div className="space-y-4 p-4 bg-mp-background rounded-lg border border-mp-border">
                    <p className="text-sm text-mp-foreground">
                        Enter your current{' '}
                        {mfaMethod === 'totp' ? 'authenticator app' : 'email'} code to confirm.
                    </p>

                    <div className="flex flex-row justify-center space-x-2">
                        {otpCode.map((digit, idx) => (
                            <MfaInput
                                key={idx}
                                index={idx}
                                value={digit}
                                onChange={handleOtpChange}
                                onKeyDown={handleOtpKeyDown}
                                onBlur={() => {}}
                                onPaste={() => {}}
                                inputRef={(el, index) => {
                                    inputsRef.current[index] = el;
                                }}
                            />
                        ))}
                    </div>

                    {error && <p className="text-sm text-mp-error text-center">{error}</p>}

                    <div className="flex gap-2">
                        <Button
                            onClick={handleRegenerate}
                            disabled={otpCode.some((d) => d === '') || isLoading}
                            size="sm"
                            className="flex-1"
                        >
                            {isLoading ? 'Verifying...' : 'Confirm regenerate'}
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleCancel}
                            disabled={isLoading}
                            className="flex-1"
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};
